import { useEffect, useMemo, useState } from "react";
import { Plural, Trans, useLingui } from "@lingui/macro";
import { ChevronDown, ChevronLeft, ChevronRight, RefreshCw, Search, X } from "lucide-react";

import { cn } from "@app/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import type {
  PathwayConditions,
  PathwayFacet,
  PathwayPractitioner,
  PathwayRange,
} from "@app/types/dashboard-pathway";
import { valueLabel } from "@app/utils/value-labels";

import { conditionWeight, isRange, labelsOf } from "./conditions";
import { type RailFacet, useSteadyFacets } from "./steadyFacets";
import { searchPeriod } from "./usePathway";

/**
 * The single filter entry point of the exploration view.
 *
 * It corrects the gap the old rail had: it could only filter on the flat index's columns while the
 * cards counted on every dimension, so "the operated ones" and "the revised ones" were things you
 * could see and not select. Both sides now read the same value service, so anything a card can draw
 * is something the rail can filter on.
 *
 * Counts are computed on the selection MINUS each facet's own condition — otherwise every unticked
 * value in a ticked group reads zero and the rail becomes a dead end.
 */

/** Values per page of a group: short enough that several open groups fit the rail at once. */
const PAGE_SIZE = 12;

/**
 * The period windows a clinician actually asks for.
 *
 * Relative, and recomputed at read time: "les patients que j'ai vus cette semaine" stops meaning
 * that the moment it is frozen into two dates. `a_venir` is the only one that looks forward, which
 * is why it cannot be expressed as a number of days back and has its own token — and it is half of
 * why doctors opened the old dashboard at all: tomorrow's list.
 *
 * They narrow on the episode's MOMENTS, so the question is "was this patient seen then", not "did
 * their problem open then". See `utils/periods.py`, which both dashboards now share.
 */
const PERIOD_TOKENS: { token: string; label: React.ReactNode }[] = [
  { token: "7j", label: <Trans>7 jours</Trans> },
  { token: "30j", label: <Trans>30 jours</Trans> },
  { token: "3m", label: <Trans>3 mois</Trans> },
  { token: "1a", label: <Trans>1 an</Trans> },
  { token: "a_venir", label: <Trans>À venir</Trans> },
];

/** Radix forbids an empty item value, so "no narrowing" needs a token of its own. */
const ALL_PRACTITIONERS = "__all__";

/** The practitioner narrowing, handed in only to a reader allowed it — admins and MAGE-X reviewers (PT-111). */
export interface RailPractitionerFilter {
  options: PathwayPractitioner[];
  selected: PathwayPractitioner | null;
  onChange: (practitioner: PathwayPractitioner | null) => void;
}

export default function PathwayFilterRail({
  facets: served,
  perimeter,
  conditions,
  activeCount,
  loading,
  periode,
  periodeStart,
  periodeEnd,
  onPeriodToken,
  onPeriodRange,
  onToggle,
  onRange,
  onClear,
  onClose,
  practitionerFilter,
}: {
  /** Undefined until the first answer, and again when one is dropped. */
  facets: PathwayFacet[] | undefined;
  /** Who and what the rail counts on, but not its conditions: a change starts the rail's layout over. */
  perimeter: string;
  conditions: PathwayConditions;
  /** Everything the rail can clear: its conditions, the period and the practitioner — the board counts it. */
  activeCount: number;
  loading: boolean;
  periode?: string | null;
  periodeStart?: string | null;
  periodeEnd?: string | null;
  onPeriodToken: (token: string | null) => void;
  onPeriodRange: (start: string | null, end: string | null) => void;
  onToggle: (dimension: string, value: string) => void;
  onRange: (dimension: string, range: PathwayRange | null) => void;
  onClear: () => void;
  onClose: () => void;
  practitionerFilter?: RailPractitionerFilter;
}) {
  const { t } = useLingui();
  const facets = useSteadyFacets(served, perimeter);
  const [search, setSearch] = useState("");
  /** Groups the reader opened, closed or ticked in: once touched, a group stays as they left it. */
  const [open, setOpen] = useState<Map<string, boolean>>(new Map());
  const [pages, setPages] = useState<Map<string, number>>(new Map());
  const practitionerSelected = practitionerFilter?.selected ?? null;

  // A stored choice the list no longer holds stays selectable, so the trigger still names it.
  const practitionerOptions = useMemo(() => {
    const listed = practitionerFilter?.options ?? [];
    if (!practitionerSelected || listed.some((p) => p.id === practitionerSelected.id)) return listed;
    return [practitionerSelected, ...listed];
  }, [practitionerFilter?.options, practitionerSelected]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return facets;
    return facets
      .map((facet) => ({
        ...facet,
        // A term is found under the words the reader sees, and under the term itself.
        values: facet.values.filter(
          (v) =>
            v.value.toLowerCase().includes(needle) ||
            valueLabel(facet.value_labels, v.value)
              .toLowerCase()
              .includes(needle),
        ),
      }))
      .filter((facet) => facet.values.length > 0 || facet.label.toLowerCase().includes(needle));
  }, [facets, search]);
  const entries = useMemo(() => railEntries(visible), [visible]);

  const setGroup = (key: string, unfolded: boolean) =>
    setOpen((current) => new Map(current).set(key, unfolded));
  const turnPage = (key: string, page: number) =>
    setPages((current) => new Map(current).set(key, page));

  return (
    // No taller than the window below the app's sticky header: the groups scroll inside instead of
    // running the rail down the page.
    <aside className="flex max-h-[calc(100vh-6rem)] w-72 shrink-0 flex-col rounded-xl border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
        <h3 className="text-xs font-semibold text-slate-900">
          <Trans>Filtres</Trans>
        </h3>
        {activeCount > 0 && (
          <span className="rounded bg-primary/10 px-1.5 py-0.5 text-3xs font-semibold text-primary tabular-nums">
            {activeCount}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="text-2xs font-medium text-slate-500 hover:text-primary"
            >
              <Trans>Tout effacer</Trans>
            </button>
          )}
          <button
            type="button"
            aria-label={t`Fermer les filtres`}
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>

      <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
        <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPages(new Map());
          }}
          placeholder={t`Rechercher une valeur...`}
          className="w-full bg-transparent text-xs outline-none placeholder:text-slate-400"
        />
      </div>

      {/* The period leads the rail. It is the filter a practitioner reaches for first, and it is
          the one that vanished when this body stopped rendering the classic toolbar controls. */}
      <div className="border-b border-slate-100 px-3 py-2">
        <p className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-slate-500">
          <Trans>Vus pendant</Trans>
        </p>
        <div className="flex flex-wrap gap-1">
          {PERIOD_TOKENS.map(({ token, label }) => {
            const active = periode === token;
            return (
              <button
                key={token}
                type="button"
                onClick={() => onPeriodToken(active ? null : token)}
                aria-pressed={active}
                className={cn(
                  "rounded-md border px-2 py-0.5 text-2xs font-medium transition-colors",
                  active
                    ? "border-primary bg-primary text-white"
                    : "border-slate-200 text-slate-600 hover:border-primary/50 hover:text-primary",
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className="mt-1.5 flex items-center gap-1.5">
          <input
            type="date"
            value={periodeStart ?? ""}
            onChange={(e) => onPeriodRange(e.target.value || null, periodeEnd ?? null)}
            aria-label={t`Début de la période`}
            className="w-full rounded border border-slate-200 px-1.5 py-0.5 text-2xs text-slate-700 outline-none focus:border-primary"
          />
          <span className="shrink-0 text-2xs text-slate-400">→</span>
          <input
            type="date"
            value={periodeEnd ?? ""}
            onChange={(e) => onPeriodRange(periodeStart ?? null, e.target.value || null)}
            aria-label={t`Fin de la période`}
            className="w-full rounded border border-slate-200 px-1.5 py-0.5 text-2xs text-slate-700 outline-none focus:border-primary"
          />
        </div>
      </div>

      {/* Who, beside when. It narrows the perimeter itself, so it comes before the facets it changes. */}
      {practitionerFilter && (
        <div className="border-b border-slate-100 px-3 py-2">
          <p className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-slate-500">
            <Trans>Praticien</Trans>
          </p>
          <Select
            value={practitionerSelected?.id ?? ALL_PRACTITIONERS}
            onValueChange={(value) =>
              practitionerFilter.onChange(
                practitionerOptions.find((p) => p.id === value) ?? null,
              )
            }
          >
            <SelectTrigger
              aria-label={t`Filtrer par praticien`}
              className={cn(
                "h-7 text-xs",
                practitionerSelected && "border-primary text-primary",
              )}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_PRACTITIONERS} className="text-xs">
                <Trans>Tous les praticiens</Trans>
              </SelectItem>
              {practitionerOptions.map((p) => (
                <SelectItem key={p.id} value={p.id} className="text-xs">
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-1 py-1 [scrollbar-gutter:stable]">
        {loading && facets.length === 0 && (
          <div className="flex items-center justify-center gap-2 py-8 text-xs text-slate-400">
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            <Trans>Chargement...</Trans>
          </div>
        )}
        {entries.map((entry) => {
          if (entry.reach) {
            // Closed until opened, like any group, unless one of its two axes carries a condition.
            const held = [entry.region, entry.segment].some(
              (f) => f !== undefined && labelsOf(conditions[f.key]).length > 0,
            );
            return (
              <ReachGroup
                key={entry.key}
                group={entry}
                conditions={conditions}
                unfolded={open.get(entry.key) ?? held}
                onUnfold={(unfolded) => setGroup(entry.key, unfolded)}
                onToggle={(dimension, value) => {
                  setGroup(entry.key, true);
                  onToggle(dimension, value);
                }}
              />
            );
          }
          const facet = entry.facet;
          const held = conditions[facet.key];
          const picked = labelsOf(held);
          const ranged = isRange(held) ? held : null;
          // A group is CLOSED until something opens it, the episode's own ones included: open, they
          // filled the rail before the reader had chosen anything. One carrying a condition opens itself,
          // so a search's condition is seen without hunting. Once the reader has touched a group, their
          // choice holds — unticking its last value no longer folds it under the cursor.
          const unfolded = open.get(facet.key) ?? (picked.length > 0 || ranged !== null);
          const pageCount = Math.max(1, Math.ceil(facet.values.length / PAGE_SIZE));
          const page = Math.min(pages.get(facet.key) ?? 0, pageCount - 1);
          const shown = unfolded ? facet.values.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE) : [];
          return (
            <div key={facet.key} className="px-2 py-1">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setGroup(facet.key, !unfolded)}
                  aria-expanded={unfolded}
                  className="flex min-w-0 flex-1 items-center gap-1 py-1 text-left"
                >
                  {unfolded ? (
                    <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-3 w-3 shrink-0 text-slate-400" />
                  )}
                  <span className="truncate text-2xs font-semibold uppercase tracking-wide text-slate-500">
                    {facet.label}
                  </span>
                  {held !== undefined && conditionWeight(held) > 0 && (
                    <span className="ml-auto shrink-0 rounded bg-primary/10 px-1 text-3xs font-semibold text-primary tabular-nums">
                      {conditionWeight(held)}
                    </span>
                  )}
                </button>
                {unfolded && pageCount > 1 && (
                  <Pager page={page} count={pageCount} onTurn={(next) => turnPage(facet.key, next)} />
                )}
              </div>
              {/* A date or a measure is also asked for by its value, not only by the bucket it is
                  drawn in: « à partir du 17/01/2022 », « au moins 75 » (PT-146). A range and the
                  ticked buckets are one condition, so setting either replaces the other. */}
              {unfolded && facet.ranges && (
                <RangeInputs
                  kind={facet.kind === "date" ? "date" : "number"}
                  range={ranged}
                  onChange={(range) => {
                    setGroup(facet.key, true);
                    onRange(facet.key, range);
                  }}
                />
              )}
              {shown.map((value) => {
                const checked = picked.includes(value.value);
                const label = valueLabel(facet.value_labels, value.value);
                const counted = value.episodes !== null;
                return (
                  <label
                    key={value.value}
                    title={counted ? label : t`${label} — non comptée dans la sélection actuelle`}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 pl-5 hover:bg-primary/5",
                      checked ? "text-primary" : !counted && "text-slate-400",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setGroup(facet.key, true);
                        onToggle(facet.key, value.value);
                      }}
                      className="h-3 w-3 shrink-0 accent-[#1752B7]"
                    />
                    <span className="min-w-0 flex-1 truncate text-xs">{label}</span>
                    <span className="shrink-0 tabular-nums text-3xs text-slate-400">{value.episodes}</span>
                  </label>
                );
              })}
              {unfolded && facet.more > 0 && (
                <p className="pl-5 text-3xs text-slate-400">
                  <Trans>+ {facet.more} valeurs non listées</Trans>
                </p>
              )}
              {/* Read in aggregate, a value too few episodes carry is left out: its wording alone can
                  be the rare thing. Said, so the list does not read as complete. */}
              {unfolded && (facet.withheld ?? 0) > 0 && (
                <p className="pl-5 text-3xs text-slate-400" title={t`Effectif inférieur au seuil d'anonymat, masqué`}>
                  <Plural
                    value={facet.withheld ?? 0}
                    one="# valeur rare masquée (seuil d'anonymat)"
                    other="# valeurs rares masquées (seuil d'anonymat)"
                  />
                </p>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

/** The region and the segment an interval field's construct reaches, gathered under the field (PT-160). */
interface ReachEntry {
  reach: true;
  key: string;
  label: string;
  region?: RailFacet;
  segment?: RailFacet;
}

type RailEntry = { reach: false; facet: RailFacet } | ReachEntry;

/** The rail's groups: one per facet, but one for the two axes an interval field is reached by. */
function railEntries(facets: RailFacet[]): RailEntry[] {
  const out: RailEntry[] = [];
  const groups = new Map<string, ReachEntry>();
  for (const facet of facets) {
    const reach = facet.reach;
    if (!reach) {
      out.push({ reach: false, facet });
      continue;
    }
    let group = groups.get(reach.of);
    if (!group) {
      group = { reach: true, key: reach.of, label: reach.label };
      groups.set(reach.of, group);
      out.push(group);
    }
    group[reach.by] = facet;
  }
  return out;
}

/**
 * An interval field as one group: its regions, then the segments of the regions in play, top to bottom.
 * Listed whole, the spine's levels were 180 constructs in no order a reader scans. A segment keeps every
 * construct that reaches it — an L4-S1 fusion is under L4-L5 — and the exact construct stays on the cards.
 */
function ReachGroup({
  group,
  conditions,
  unfolded,
  onUnfold,
  onToggle,
}: {
  group: ReachEntry;
  conditions: PathwayConditions;
  unfolded: boolean;
  onUnfold: (unfolded: boolean) => void;
  onToggle: (dimension: string, value: string) => void;
}) {
  const { t } = useLingui();
  const { region, segment } = group;
  const regions = region ? labelsOf(conditions[region.key]) : [];
  const segments = segment ? labelsOf(conditions[segment.key]) : [];
  const within = segment?.reach?.regions ?? {};
  // The regions ticked, and those of a ticked segment: a segment the search ticked alone stays in view.
  const inPlay = new Set([...regions, ...segments.flatMap((s) => (within[s] ? [within[s]] : []))]);
  // Without a region to choose — none declared, or none left by the rail's search — every segment shows.
  const everySegment = !region || region.values.length === 0;
  const shownSegments = (segment?.values ?? []).filter(
    (v) => everySegment || inPlay.has(within[v.value]) || segments.includes(v.value),
  );
  const weight = regions.length + segments.length;
  const withheld = (region?.withheld ?? 0) + (segment?.withheld ?? 0);

  return (
    <div className="px-2 py-1">
      <button
        type="button"
        onClick={() => onUnfold(!unfolded)}
        aria-expanded={unfolded}
        className="flex w-full min-w-0 items-center gap-1 py-1 text-left"
      >
        {unfolded ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0 text-slate-400" />
        )}
        <span className="truncate text-2xs font-semibold uppercase tracking-wide text-slate-500">
          {group.label}
        </span>
        {weight > 0 && (
          <span className="ml-auto shrink-0 rounded bg-primary/10 px-1 text-3xs font-semibold text-primary tabular-nums">
            {weight}
          </span>
        )}
      </button>
      {unfolded && (
        <div className="space-y-1.5 pb-1 pl-5">
          {region && region.values.length > 0 && (
            <ReachPills facet={region} picked={regions} onToggle={onToggle} />
          )}
          {segment && shownSegments.length > 0 && (
            <div>
              <p className="mb-1 text-3xs font-medium text-slate-400">
                <Trans>Segments touchés</Trans>
              </p>
              <ReachPills facet={{ ...segment, values: shownSegments }} picked={segments} onToggle={onToggle} />
            </div>
          )}
          {segment && shownSegments.length === 0 && !everySegment && (
            <p className="text-3xs text-slate-400">
              <Trans>Choisir une région pour voir ses segments.</Trans>
            </p>
          )}
          {withheld > 0 && (
            <p className="text-3xs text-slate-400" title={t`Effectif inférieur au seuil d'anonymat, masqué`}>
              <Plural
                value={withheld}
                one="# valeur rare masquée (seuil d'anonymat)"
                other="# valeurs rares masquées (seuil d'anonymat)"
              />
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** A facet's values as pills, each with its count — short labels a row holds several of. */
function ReachPills({
  facet,
  picked,
  onToggle,
}: {
  facet: RailFacet;
  picked: string[];
  onToggle: (dimension: string, value: string) => void;
}) {
  const { t } = useLingui();
  return (
    <div className="flex flex-wrap gap-1">
      {facet.values.map((value) => {
        const label = value.value;
        const active = picked.includes(label);
        const counted = value.episodes !== null;
        return (
          <button
            key={label}
            type="button"
            onClick={() => onToggle(facet.key, label)}
            aria-pressed={active}
            title={counted ? facet.label : t`${label} — non comptée dans la sélection actuelle`}
            className={cn(
              "rounded-md border px-2 py-0.5 text-2xs font-medium transition-colors",
              active
                ? "border-primary bg-primary text-white"
                : counted
                  ? "border-slate-200 text-slate-600 hover:border-primary/50 hover:text-primary"
                  : "border-slate-200 text-slate-400 hover:border-primary/50",
            )}
          >
            {label}
            {counted && <span className="ml-1 tabular-nums opacity-70">{value.episodes}</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A group's pages, in its header, where turning one cannot move it: below the values, a shorter last
 * page pulled the pager up from under the cursor.
 */
function Pager({ page, count, onTurn }: { page: number; count: number; onTurn: (page: number) => void }) {
  const { t } = useLingui();
  const arrow =
    "rounded p-0.5 hover:bg-slate-100 hover:text-primary disabled:pointer-events-none disabled:opacity-40";
  return (
    <div className="flex shrink-0 items-center text-3xs tabular-nums text-slate-400">
      <button
        type="button"
        aria-label={t`Valeurs précédentes`}
        disabled={page === 0}
        onClick={() => onTurn(page - 1)}
        className={arrow}
      >
        <ChevronLeft className="h-3 w-3" />
      </button>
      <span>
        {page + 1}/{count}
      </span>
      <button
        type="button"
        aria-label={t`Valeurs suivantes`}
        disabled={page === count - 1}
        onClick={() => onTurn(page + 1)}
        className={arrow}
      >
        <ChevronRight className="h-3 w-3" />
      </button>
    </div>
  );
}

/** A day as a date input holds it: a bound written to the month or the year read as its first or last day. */
function asDay(bound: string | number | undefined, upper: boolean): string {
  const text = bound === undefined ? "" : `${bound}`.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const span = text ? searchPeriod(text) : null;
  return span && "start" in span ? (upper ? span.end : span.start) : "";
}

/** A bound as its field shows it. */
function boundField(kind: "date" | "number", bound: string | number | undefined, upper: boolean): string {
  if (kind === "date") return asDay(bound, upper);
  return bound === undefined ? "" : `${bound}`;
}

/** A field's text as a bound: `undefined` when empty, `null` when it reads as none. */
function boundOf(kind: "date" | "number", text: string): string | number | undefined | null {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  if (kind === "date") return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : null;
  const number = Number(trimmed.replace(",", "."));
  return Number.isFinite(number) ? number : null;
}

/**
 * The two bounds of a range, applied when the reader leaves a field or presses Enter — so typing « 75 »,
 * or a year digit by digit in a date field, does not query the board at every keystroke. Bounds written
 * the wrong way round are put in order; a number takes a decimal comma; an unreadable or unfinished one
 * is marked and left unapplied rather than read as an empty bound.
 */
function RangeInputs({
  kind,
  range,
  onChange,
}: {
  kind: "date" | "number";
  range: PathwayRange | null;
  onChange: (range: PathwayRange | null) => void;
}) {
  const { t } = useLingui();
  const shownMin = boundField(kind, range?.min, false);
  const shownMax = boundField(kind, range?.max, true);
  const [draft, setDraft] = useState({ min: shownMin, max: shownMax });
  // A date field the browser holds half-typed reports an empty value: told apart by its own validity.
  const [unfinished, setUnfinished] = useState({ min: false, max: false });
  useEffect(() => {
    setDraft({ min: shownMin, max: shownMax });
    setUnfinished({ min: false, max: false });
  }, [shownMin, shownMax]);

  const unreadable = {
    min: unfinished.min || boundOf(kind, draft.min) === null,
    max: unfinished.max || boundOf(kind, draft.max) === null,
  };
  const commit = () => {
    if (unreadable.min || unreadable.max) return;
    // Left as it is shown, a field applies nothing: a year bound is not rewritten as its first day.
    if (draft.min === shownMin && draft.max === shownMax) return;
    let min = boundOf(kind, draft.min) ?? undefined;
    let max = boundOf(kind, draft.max) ?? undefined;
    if (min !== undefined && max !== undefined && min > max) [min, max] = [max, min];
    onChange(min === undefined && max === undefined ? null : { min, max });
  };
  const field = (side: "min" | "max") => ({
    value: draft[side],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, validity } = e.target;
      setDraft((current) => ({ ...current, [side]: value }));
      setUnfinished((current) => ({ ...current, [side]: validity.badInput }));
    },
    onBlur: commit,
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") commit();
    },
    "aria-invalid": unreadable[side],
    className: cn(
      "w-full rounded border px-1.5 py-0.5 text-2xs text-slate-700 outline-none focus:border-primary",
      unreadable[side] ? "border-red-300" : "border-slate-200",
    ),
  });

  return (
    <div className="mb-1 flex items-center gap-1.5 pl-5">
      {kind === "date" ? (
        <input type="date" aria-label={t`À partir du`} {...field("min")} />
      ) : (
        <input
          type="text"
          inputMode="decimal"
          placeholder={t`min.`}
          aria-label={t`Valeur minimale`}
          {...field("min")}
        />
      )}
      <span className="shrink-0 text-2xs text-slate-400">→</span>
      {kind === "date" ? (
        <input type="date" aria-label={t`Jusqu'au`} {...field("max")} />
      ) : (
        <input
          type="text"
          inputMode="decimal"
          placeholder={t`max.`}
          aria-label={t`Valeur maximale`}
          {...field("max")}
        />
      )}
    </div>
  );
}
